// frontend/src/api/familyDoctors.ts
// Public UI — Family Doctor search + filter lookups

export class ApiError extends Error {
  status: number;
  payload?: any;

  constructor(status: number, message: string, payload?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: {
      ...(init?.headers || {}),
    },
    ...init,
  });

  const text = await res.text().catch(() => "");
  let data: any = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text || null;
  }

  if (!res.ok) {
    const message =
      (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

function normalizeId(val: any): number {
  const n = typeof val === "number" ? val : Number(String(val ?? "").trim());
  return Number.isFinite(n) ? n : 0;
}

/* -----------------------------
   Lookup types (matches API)
------------------------------ */

export type City = {
  CityID: number;
  Name: string;
  RegionID?: number | null;
};

export type ClinicSetting = {
  ClinicSettingID: number;
  Name: string;
};

export type Language = {
  LanguageID: number;
  Name: string;
};

export type VisitType = {
  VisitTypeID: number;
  Name: string;
};

export type CoverageType = {
  CoverageTypeID: number;
  Name: string;
};

export type AccessibilityFeature = {
  AccessibilityFeatureID: number;
  Name: string;
};

export type Clinic = {
  ClinicID: number;
  Name: string;
  CityID?: number | null;
  CityName?: string | null;
  AddressLine1?: string | null;
  AddressLine2?: string | null;
  PostalCode?: string | null;
  Phone?: string | null;
  Fax?: string | null;
  Website?: string | null;
  ClinicSettingID?: number | null;
  Latitude?: number | null;
  Longitude?: number | null;
};

export type OnSiteService = {
  OnSiteServiceID: number;
  Name: string;
};

/* -----------------------------
   Lookup API calls
------------------------------ */

export async function getCities(): Promise<City[]> {
  const data = await requestJson<any>("/api/cities", { method: "GET" });
  return Array.isArray(data) ? (data as City[]) : [];
}

export async function getClinicSettings(): Promise<ClinicSetting[]> {
  const data = await requestJson<any>("/api/clinic-settings", { method: "GET" });
  return Array.isArray(data) ? (data as ClinicSetting[]) : [];
}

export async function getLanguages(): Promise<Language[]> {
  const data = await requestJson<any>("/api/languages", { method: "GET" });
  return Array.isArray(data) ? (data as Language[]) : [];
}

export async function getVisitTypes(): Promise<VisitType[]> {
  const data = await requestJson<any>("/api/visit-types", { method: "GET" });
  return Array.isArray(data) ? (data as VisitType[]) : [];
}

export async function getCoverageTypes(): Promise<CoverageType[]> {
  const data = await requestJson<any>("/api/coverage-types", { method: "GET" });
  return Array.isArray(data) ? (data as CoverageType[]) : [];
}

export async function getAccessibilityFeatures(): Promise<AccessibilityFeature[]> {
  const data = await requestJson<any>("/api/accessibility-features", {
    method: "GET",
  });
  return Array.isArray(data) ? (data as AccessibilityFeature[]) : [];
}

export async function getClinics(): Promise<Clinic[]> {
  const data = await requestJson<any>("/api/clinics", { method: "GET" });
  return Array.isArray(data) ? (data as Clinic[]) : [];
}

/**
 * Backend endpoint we added:
 * GET /api/on-site-services
 */
export async function getOnSiteServices(): Promise<OnSiteService[]> {
  const data = await requestJson<any>("/api/on-site-services", { method: "GET" });
  return Array.isArray(data) ? (data as OnSiteService[]) : [];
}

/* -----------------------------
   Search API call (v1 -> v1.1 filters-ready)
------------------------------ */

export type FamilyDoctorSearchItem = {
  FamilyDoctorID: number;
  FullName: string;
  ClinicID: number;
  AcceptingNewPatients: boolean;
  CreatedAt: string;

  // v1.1 enrichment (backend now sends these; kept optional for backward compatibility)
  ClinicName?: string | null;
  CityName?: string | null;
  Languages?: string[];
  DistanceKm?: number | null;

  // v1.6 map enrichment (clinic coordinates for Mapbox markers)
  ClinicLatitude?: number | null;
  ClinicLongitude?: number | null;

  // heartbeat (backend may include this)
  UpdatedAt?: string | null;
};

export type FamilyDoctorSearchFilters = {
  /**
   * IMPORTANT semantics:
   * - true  => filter to ONLY accepting doctors
   * - undefined => do NOT filter by accepting status (show all)
   *
   * Do NOT send "false" to backend, because backend treats false as
   * "only NOT accepting" which is not what the UI toggle means.
   */
  acceptingNewPatientsOnly?: boolean;
  city?: string;
  postalCode?: string;
  radiusKm?: number;
  doctorGender?: "Female" | "Male";
  language?: string;

  // v1.2 doctor-level filter IDs
  visitTypeId?: number;
  coverageTypeId?: number;

  // v1.3 clinic-level filter ID
  clinicSettingId?: number;

  // v1.4 clinic-level filter ID
  accessibilityFeatureId?: number;

  // v1.5 clinic-level filter ID
  onSiteServiceId?: number;
};

function buildQueryString(filters?: FamilyDoctorSearchFilters): string {
  if (!filters) return "";
  const p = new URLSearchParams();

  // ✅ Critical fix:
  // Only send this param when TRUE.
  // When toggle is OFF, we omit it entirely so backend returns ALL doctors.
  if (filters.acceptingNewPatientsOnly === true) {
    p.set("acceptingNewPatientsOnly", "true");
  }

  if (filters.city) p.set("city", filters.city);
  if (filters.postalCode) p.set("postalCode", filters.postalCode);
  if (filters.radiusKm !== undefined && Number.isFinite(filters.radiusKm)) {
    p.set("radiusKm", String(filters.radiusKm));
  }
  if (filters.doctorGender) p.set("doctorGender", filters.doctorGender);
  if (filters.language) p.set("language", filters.language);

  // doctor-level filter IDs
  if (filters.visitTypeId !== undefined && Number.isFinite(filters.visitTypeId)) {
    p.set("visitTypeId", String(filters.visitTypeId));
  }
  if (
    filters.coverageTypeId !== undefined &&
    Number.isFinite(filters.coverageTypeId)
  ) {
    p.set("coverageTypeId", String(filters.coverageTypeId));
  }

  // clinic-level filter ID
  if (
    filters.clinicSettingId !== undefined &&
    Number.isFinite(filters.clinicSettingId)
  ) {
    p.set("clinicSettingId", String(filters.clinicSettingId));
  }

  // clinic-level filter ID
  if (
    filters.accessibilityFeatureId !== undefined &&
    Number.isFinite(filters.accessibilityFeatureId)
  ) {
    p.set("accessibilityFeatureId", String(filters.accessibilityFeatureId));
  }

  // clinic-level filter ID
  if (
    filters.onSiteServiceId !== undefined &&
    Number.isFinite(filters.onSiteServiceId)
  ) {
    p.set("onSiteServiceId", String(filters.onSiteServiceId));
  }

  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Backend endpoint:
 * GET /api/family-doctors/search
 */
export async function searchFamilyDoctors(
  filters?: FamilyDoctorSearchFilters
): Promise<FamilyDoctorSearchItem[]> {
  const qs = buildQueryString(filters);
  const data = await requestJson<any>(`/api/family-doctors/search${qs}`, {
    method: "GET",
  });

  // Backward compatible: tolerate any payload shape but only return arrays
  if (!Array.isArray(data)) return [];

  // Soft-normalize Languages to always be an array when present
  return (data as any[]).map((row) => {
    const languages =
      Array.isArray(row?.Languages) ? (row.Languages as string[]) : undefined;

    return {
      ...row,
      Languages: languages,
    } as FamilyDoctorSearchItem;
  });
}

/* -----------------------------
   Public Profile + Request To Be A Patient (Phase 1)
------------------------------ */

export type ClinicRegularHoursItem = {
  dayOfWeek: number; // 0=Sunday..6=Saturday
  isClosed: boolean;
  open: string | null; // "HH:MM"
  close: string | null; // "HH:MM"
};

export type ClinicHoursClosureItem = {
  date: string; // YYYY-MM-DD
  reason: string | null;
};

export type ClinicHoursDay = {
  isClosed: boolean;
  open?: string;
  close?: string;
};

export type ClinicTemporaryOverride = {
  start: string | null; // YYYY-MM-DD
  end: string | null; // YYYY-MM-DD
  notePublic: string | null;
  hoursByDay: Record<string, ClinicHoursDay>;
};

export type ClinicWalkInHours = {
  enabled: boolean;
  notePublic: string | null;
  hoursByDay: Record<string, ClinicHoursDay>;
};

export type ClinicHoursMeta = {
  Closures: ClinicHoursClosureItem[];
  TemporaryOverride: ClinicTemporaryOverride | null;
  WalkIn: ClinicWalkInHours | null;
};

export type FamilyDoctorPublicProfile = {
  FamilyDoctorID: number;
  FullName: string;
  Gender?: string | null;
  AcceptingNewPatients: boolean;

  Clinic: {
    ClinicID: number;
    ClinicName: string;
    AddressLine1?: string | null;
    AddressLine2?: string | null;
    PostalCode?: string | null;
    Phone?: string | null;
    Fax?: string | null;
    Website?: string | null;
    CityName?: string | null;

    // ✅ New (additive)
    ClinicSettingName?: string | null;
    Latitude?: number | null;
    Longitude?: number | null;
  };

  Languages: string[];
  VisitTypes: string[];
  CoverageTypes: string[];
  AccessibilityFeatures: string[];
  OnSiteServices: string[];

  // ✅ New (additive)
  ClinicRegularHours: ClinicRegularHoursItem[];
  ClinicHoursMeta: ClinicHoursMeta;
};

export async function getFamilyDoctorPublicProfile(
  id: number | string
): Promise<FamilyDoctorPublicProfile> {
  const fdId = normalizeId(id);
  return requestJson<FamilyDoctorPublicProfile>(
    `/api/family-doctor-profile?id=${fdId}`,
    { method: "GET" }
  );
}

export type PatientRequestReasonItem = {
  PatientRequestReasonID: number;
  Name: string;
  SortOrder: number;
};

export async function getPatientRequestReasons(): Promise<
  PatientRequestReasonItem[]
> {
  const data = await requestJson<any>("/api/patient-request-reasons", {
    method: "GET",
  });
  const list = Array.isArray(data) ? (data as PatientRequestReasonItem[]) : [];
  // stable sort for UI even if DB order changes
  return list.slice().sort((a, b) => {
    const ao = Number.isFinite(a?.SortOrder) ? a.SortOrder : 0;
    const bo = Number.isFinite(b?.SortOrder) ? b.SortOrder : 0;
    if (ao !== bo) return ao - bo;
    return (a?.PatientRequestReasonID || 0) - (b?.PatientRequestReasonID || 0);
  });
}

/* -----------------------------
   Patient Request Flow (Phase 2)
------------------------------ */

export type PatientGenderOption =
  | "Female"
  | "Male"
  | "Non-binary"
  | "Prefer not to say";

export type CreatePatientRequestPayload = {
  familyDoctorId: number;

  // ✅ NEW (locked): split name + applicant contact
  firstName: string;
  lastName: string;
  applicantEmail: string;
  applicantPhone: string;

  // ✅ NEW (MANDATORY): patient gender
  patientGender: PatientGenderOption;

  dateOfBirth: string; // YYYY-MM-DD
  postalCode: string;

  // ✅ NEW (locked): required
  coverageTypeId: number;

  // ✅ NEW (locked): consent
  consentAccepted: boolean;
  consentVersion?: string;

  reasonIds?: number[];

  // Backward compatibility (optional, can be removed later):
  fullName?: string;
  email?: string;
  phoneNumber?: string;
};

export type CreatePatientRequestResponse = {
  registrationRequestId: number;
  token: string; // manage token
};

/**
 * Backend route (Azure Function):
 * POST /api/patient/requests
 */
export async function createPatientRequest(
  payload: CreatePatientRequestPayload
): Promise<CreatePatientRequestResponse> {
  return requestJson<CreatePatientRequestResponse>("/api/patient/requests", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type PatientRequestStatusResponse = {
  requestId: number;
  status: string;

  doctor: {
    name: string;
  };

  clinic: { name: string } | null;

  timeline: {
    createdAt: string | null;
    submittedAt: string | null;
    verifiedAt?: string | null; // ✅ NEW (optional for backward compatibility)
    decisionAt: string | null;
    cancelledAt: string | null;
    autoExpiredAt: string | null;
  };

  token: {
    expiresAt: string | null;
    isUsed: boolean;
  };
};

/**
 * Backend route (Azure Function):
 * GET /api/patient/requests/{token}
 */
export async function getPatientRequestByToken(
  token: string
): Promise<PatientRequestStatusResponse> {
  const safeToken = String(token || "").trim();
  return requestJson<PatientRequestStatusResponse>(
    `/api/patient/requests/${encodeURIComponent(safeToken)}`,
    { method: "GET" }
  );
}

export type CancelPatientRequestResponse = {
  success: boolean;
};

/**
 * Backend route (Azure Function):
 * POST /api/patient/requests/{token}/cancel
 */
export async function cancelPatientRequestByToken(
  token: string
): Promise<CancelPatientRequestResponse> {
  const safeToken = String(token || "").trim();
  return requestJson<CancelPatientRequestResponse>(
    `/api/patient/requests/${encodeURIComponent(safeToken)}/cancel`,
    { method: "POST" }
  );
}

/**
 * Optional (future): if we ever want frontend-triggered verify (not required for email link flow).
 * Backend route:
 * GET/POST /api/patient-request-verify-email?token=...
 */
export async function verifyPatientRequestEmail(
  token: string
): Promise<{
  registrationRequestId: number;
  verified: boolean;
  alreadyVerified?: boolean;
  token?: string | null;
}> {
  const safeToken = String(token || "").trim();
  return requestJson<any>(
    `/api/patient-request-verify-email?token=${encodeURIComponent(safeToken)}`,
    { method: "GET" }
  );
}
